// ─────────────────────────────────────────────────────────────────────────────────────────────────
// JoltWorld — a lazy-loaded rigid-body physics world for ONE Factory's ore (Phase 1: movement only).
//
// Design decisions locked by the spike (see PHYSICS_JOLT.md §11–§12):
//   • stock single-thread `jolt-physics` (NO native build, NO multithread — cost is solver-bound, not the
//     JS listener; and MT is blocked by atproto/COEP anyway).
//   • ore are DYNAMIC bodies with a NO-TUMBLE DOF lock (lock RotX/RotZ, keep Y + slide + yaw) — this keeps
//     the friction conveyor working while stopping the vertical pile-up that corrupts the py→floor derive.
//   • belts are STATIC bodies; a JS ContactListener stamps surface velocity onto belt↔ore contacts.
//   • sleeping ON (settled/jammed piles cost ~0); ore on their own object/broadphase layer.
//
// The economy (capKey-gated value hooks) stays in factory.ts and runs as a pass over the positions this
// world reads back. This module only owns POSITION.
//
// UNITS: we simulate in METERS (game units ÷ SCALE) so Jolt's default tolerances (speculative distance,
// sleep thresholds ~cm-scale) stay valid. All public methods take/return GAME units; conversion is internal.
// ─────────────────────────────────────────────────────────────────────────────────────────────────
import { FACTORY, PHYS, DIR_VEC, type Dir } from "./defs";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type J = any; // the initialized Jolt module — its generated d.ts is heavy; we pin behavior, not types.

const SCALE = 100; // 1 Jolt meter = 100 game units = 1 tile. game→jolt: ÷SCALE; jolt→game: ×SCALE.
// Ore collider half-extent in GAME units (~0.24 tile) — what makes belt capacity real. Live-tunable via PHYS.half.
const oreHalf = (): number => PHYS.half;
const BELT_HALF_Y = 20; // belt slab half-thickness (game); its TOP sits ORE_HALF below deckY so ore rest with center AT deckY.
const L_STATIC = 0, L_ORE = 1, NUM_OL = 2;
const BP_STATIC = 0, BP_ORE = 1, NUM_BP = 2;

let joltImport: Promise<{ default: (cfg?: unknown) => Promise<J> }> | null = null;
/** Lazy dynamic import (the ~900KB chunk loads once, on first battle), then a FRESH Emscripten module per world.
 *  Each JoltWorld gets its own module instance: tearing a world down discards its module wholesale, so a
 *  destroyed world's ContactListenerJS/body tables can never corrupt the next world on a shared module
 *  (cross-world "table index out of bounds"). The one-module-two-systems RAM optimization is deferred (§4). */
function loadJolt(): Promise<J> {
  if (!joltImport) joltImport = import("jolt-physics") as unknown as Promise<{ default: (cfg?: unknown) => Promise<J> }>;
  return joltImport.then((m) => m.default());
}

/** Opaque per-ore handle the Factory stores on its Item (o.bodyH). */
export type OreHandle = number;

export class JoltWorld {
  private J: J;
  private jolt: J;              // JoltInterface
  private ps: J;                // PhysicsSystem
  private bi: J;                // BodyInterface
  private oreShape: J;          // shared box shape for every ore
  private listener: J = null; // created lazily on the first registerBelt (Increment 3)
  private beltVel = new Map<number, J>(); // belt bodyID(index+seq) → Jolt.Vec3 surface velocity (jolt units)
  private beltCell = new Map<number, string>();          // belt bodyID → the cell key "gx,gz,floor" it belongs to
  private oreCell = new Map<number, { cell: string; w: number }>(); // ore bodyID → belt cell it is MOST on, this step
  private oreCellPrev = new Map<number, { cell: string; w: number }>(); // ...and last step, since contacts arrive unordered
  private oreDrive = new Map<number, [number, number, number]>();     // ore bodyID → SUM of the surface velocities
  private oreDrivePrev = new Map<number, [number, number, number]>(); //   of every deck it touches (jolt units)
  private cellVel = new Map<string, J>();                // cell key → that deck's surface velocity (shared with beltVel)
  private wallId = new Map<number, string>();            // wall/curb bodyID → its key (so a probe can name what an ore leans on)
  /** DEV contact census: ore bodyID → what it is touching this step. null (default) = not gathering, zero cost.
   *  An ore stalled on a driven belt looks identical from outside to one riding it; this is the only way to see
   *  whether it is jammed against a curb, held up by other ore, or genuinely just sitting on the deck. */
  private probe: Map<number, { belt: number; wall: number; curb: number; ore: number; nyMin: number }> | null = null;
  private belts = new Map<string, J>();   // cell key "gx,gz,floor" → static belt Body (for syncBelts add/remove)
  private beltSig = new Map<string, string>(); // ...and the SHAPE+HEADING+SPEED that body was built from, so a
                                          // cell whose deck CHANGES (re-import, a corner re-themed, a ramp
                                          // re-laid) rebuilds instead of silently keeping the old body.
  private wallBodies = new Map<string, J>(); // "gx,gz,floor|dir" → static wall slab (ore containment)
  private bodies = new Map<OreHandle, J>(); // live ore handle → Body (in play)
  private freeList: J[] = [];   // parked/deactivated ore bodies ready to reuse (pre-created; NO per-ore create/destroy)
  private nextH: OreHandle = 1;
  private dof: number;
  private _rp: J;               // scratch RVec3 (re-positioning) — reused so we never churn temp allocations
  private _sv: J;               // scratch Vec3 (velocity)
  private _iq: J;               // identity Quat
  private _rail: J;             // scratch Vec3 for the scaled guide-rail surface velocity (per-contact, hot path)
  private accum = 0;            // fixed-timestep accumulator (seconds)
  private readonly fixed = 1 / 30;
  private readonly PARK = -10000; // jolt-space Y a parked body sits at, far under any play area

  /** STATIC-body ceiling, derived from the plate rather than guessed. Every buildable cell can carry a deck,
   *  and WALLS stack up to one per tile edge — so the old flat "+512" was short by roughly 4x and a large base
   *  ran CreateBody past mMaxBodies, which faults the WASM heap instead of failing soft (the exact trap the ore
   *  pool documents at spawnOre). Reproduced as `memory access out of bounds` in DestroyBody after 1440 walls.
   *    decks  : 1 per cell, x2 for ramp bodies + their separate flat crest belts
   *    walls  : up to 4 edges per tile
   *    curbs  : up to 4 per DECKED cell — these also go through registerWall (key prefix "curb:") and are easy
   *             to forget: a max-walls build measured 1920 static bodies for 1800 buildings, the extra 120
   *             being curbs. A sparse/checkerboard layout curbs far more edges than a solid one.
   *    slack  : pad / ground / anything added later
   *  Static bodies never pair with each other (the layer filter only enables STATIC-vs-ORE), so raising this
   *  costs body storage only — mMaxBodyPairs and mMaxContactConstraints are unaffected. */
  private static readonly CELLS = FACTORY.footprint * FACTORY.footprint * FACTORY.maxFloors;
  private static readonly STATIC_CAP = JoltWorld.CELLS * (2 /*decks*/ + 4 /*walls*/ + 4 /*curbs*/) + 64;
  private staticCount = 0;      // live static bodies (belts + ramps + walls), against STATIC_CAP
  private staticWarned = false; // log the exhaustion once, not every frame

  /** Create a STATIC body against the budget. Returns null when spent — callers MUST bail rather than use it.
   *  Never bypass this: CreateBody past mMaxBodies corrupts the WASM heap instead of returning an error. */
  private createStatic(bcs: J): J | null {
    if (this.staticCount >= JoltWorld.STATIC_CAP) {
      if (!this.staticWarned) {
        this.staticWarned = true;
        console.warn(`[jolt] static body budget spent (${JoltWorld.STATIC_CAP}) — further belts/walls are SKIPPED; ore will fall through them. Raise JoltWorld.STATIC_CAP.`);
      }
      return null;
    }
    const body = this.bi.CreateBody(bcs);
    if (!body) return null;     // belt-and-braces: Jolt itself refused
    this.staticCount++;
    return body;
  }

  private constructor(J: J) {
    this.J = J;
    const s = new J.JoltSettings();
    s.mMaxBodies = FACTORY.oreCap + JoltWorld.STATIC_CAP; // ore pool + EVERY deck and wall the plate can hold
    s.mMaxBodyPairs = 32768;
    s.mMaxContactConstraints = 16384;         // ~7.6MB solver scratch — fits the stock fixed temp allocator

    const of = new J.ObjectLayerPairFilterTable(NUM_OL);
    of.EnableCollision(L_STATIC, L_ORE);
    of.EnableCollision(L_ORE, L_ORE);
    const bp = new J.BroadPhaseLayerInterfaceTable(NUM_OL, NUM_BP);
    bp.MapObjectToBroadPhaseLayer(L_STATIC, new J.BroadPhaseLayer(BP_STATIC));
    bp.MapObjectToBroadPhaseLayer(L_ORE, new J.BroadPhaseLayer(BP_ORE));
    s.mObjectLayerPairFilter = of;
    s.mBroadPhaseLayerInterface = bp;
    s.mObjectVsBroadPhaseLayerFilter = new J.ObjectVsBroadPhaseLayerFilterTable(bp, NUM_BP, of, NUM_OL);

    this.jolt = new J.JoltInterface(s);
    J.destroy(s);
    this.ps = this.jolt.GetPhysicsSystem();
    this.bi = this.ps.GetBodyInterface();
    this.ps.SetGravity(new J.Vec3(0, -PHYS.gravity / SCALE, 0));

    // Full 6-DOF lets ore tip and roll like real parts. The alternative (lock RotX|RotZ, keep translation + yaw)
    // keeps piles tidy and the py→floor derivation safe, at the cost of ore that never tumble.
    this.dof = PHYS.tumble
      ? J.EAllowedDOFs_All
      : J.EAllowedDOFs_TranslationX | J.EAllowedDOFs_TranslationY | J.EAllowedDOFs_TranslationZ | J.EAllowedDOFs_RotationY;

    // Collider shape is a FEEL knob: a box catches its corners on belts, a rounded box or sphere rolls.
    const half = oreHalf() / SCALE;
    this.oreShape = PHYS.shape === 2
      ? new J.SphereShape(half)
      : new J.BoxShape(new J.Vec3(half, half, half), PHYS.shape === 1 ? half * 0.5 : Math.min(0.02, half * 0.4));

    // Pre-create the ore body POOL once (reused via activate/park). Per-ore CreateBody/DestroyBody churn over a
    // sustained run corrupts the WASM heap ("function signature mismatch"); a fixed pool avoids it entirely.
    this._rp = new J.RVec3(0, this.PARK, 0);
    this._sv = new J.Vec3(0, 0, 0);
    this._iq = new J.Quat(0, 0, 0, 1);
    this._rail = new J.Vec3(0, 0, 0);
    for (let i = 0; i < FACTORY.oreCap; i++) {
      const bcs = new J.BodyCreationSettings(this.oreShape, this._rp, this._iq, J.EMotionType_Dynamic, L_ORE);
      bcs.mAllowedDOFs = this.dof;
      // CCD stops fast flung/falling ore tunnelling, but a linear cast places the body at the FIRST hit along
      // its sweep — an ore resting a hair inside a wall gets clamped to its own position every step and never
      // moves again, which is the "pinned against a parallel wall" bug. Roblox parts step discretely.
      bcs.mMotionQuality = PHYS.ccd ? J.EMotionQuality_LinearCast : J.EMotionQuality_Discrete;
      // Ore never sleep. Roblox's sleep test needs an assembly to be under a velocity AND an IMPULSE threshold,
      // so a part a conveyor is pushing never qualifies — ours would, and a slept body stops generating the
      // contact callbacks the conveyor drives it through, freezing it into a permanent dam. Same semantics, one line.
      bcs.mAllowSleeping = false;
      bcs.mFriction = PHYS.friction;      // grip the belt so surface velocity drives it up to speed quickly
      bcs.mRestitution = PHYS.restitution; // bounce → loses belt contact, and with it drive + throughput
      // Cheap-ore solver: since Jolt 5.0 an island where EVERY body asks for fewer iterations runs at that lower
      // count, so a pure-ore clog solves far cheaper while any island touching real machinery keeps full precision.
      // ...but an ore pinned against a barrier has FOUR simultaneous constraints (belt ×2, curb, wall) of which
      // only some carry the conveyor's surface velocity, and too few iterations can leave that friction system
      // unresolved — the body simply never gets driven. Tunable so the trade can be measured, not guessed.
      bcs.mNumVelocityStepsOverride = PHYS.velSteps; // Jolt default 10
      bcs.mNumPositionStepsOverride = PHYS.posSteps; // Jolt default 2
      const body = this.bi.CreateBody(bcs);
      J.destroy(bcs);
      this.bi.AddBody(body.GetID(), J.EActivation_DontActivate);
      this.freeList.push(body);
    }
  }

  /** The belt conveyor needs a contact listener; create it ONCE, lazily, the first time a belt is registered
   *  (Increment 3). Deferred so movement-only ore (Increment 2, no belts) never pays the JS↔WASM callback path. */
  private ensureListener(): void {
    if (this.listener) return;
    const J = this.J;
    const cl = new J.ContactListenerJS();
    const stamp = (inB1: number, inB2: number, inManifold: number, ioSettings: number) => {
      const id1 = J.wrapPointer(inB1, J.Body).GetID().GetIndexAndSequenceNumber();
      const id2 = J.wrapPointer(inB2, J.Body).GetID().GetIndexAndSequenceNumber();
      const bv = this.beltVel.get(id1) ?? this.beltVel.get(id2);
      let v = bv;
      if (bv) {
        // Remember the TOTAL drive on this ore. An ore straddling a tile seam rests on two decks at once, and
        // where those decks run perpendicular (a corner) their pushes are quite different directions — which
        // matters below, because the addressed cell alone is a bad answer to "which way is this ore going".
        const oreId = this.beltVel.has(id1) ? id2 : id1;
        const d = this.oreDrive.get(oreId);
        if (d) { d[0] += bv.GetX(); d[1] += bv.GetY(); d[2] += bv.GetZ(); }
        else this.oreDrive.set(oreId, [bv.GetX(), bv.GetY(), bv.GetZ()]);
      } else {
        // GUIDE RAILS, not brakes. A wall/curb contact has no surface velocity, so its friction acts against
        // whatever the belt is doing — and when the barrier runs PARALLEL to the drive that friction is pure
        // braking. Measured: an ore held perfectly motionless for 2 s with nothing behind it, on a driven belt.
        // Give the rail the ore's drive so it stops fighting the belt; containment is untouched because the
        // NORMAL direction is unchanged. That is what a walled Miner's Haven conveyor does: ore run ALONG it.
        //
        // The drive is PROJECTED onto the rail's own plane, because a surface can only move within itself.
        // Feeding it the raw cell velocity was worse than useless at a corner: ore 264 sat on a +X deck and a
        // -Z deck at once, the -Z one drove it into a curb, and the "assist" handed that curb a velocity
        // pointing straight into its own face — zero help against the +X drive it was busy canceling.
        const wid = this.wallId.has(id1) ? id1 : this.wallId.has(id2) ? id2 : 0;
        if (wid && PHYS.railAssist > 0) {
          const oreId = wid === id1 ? id2 : id1;
          // contacts arrive unordered within a step, so fall back to what it was riding last step
          const d = this.oreDrive.get(oreId) ?? this.oreDrivePrev.get(oreId);
          if (d) {
            let nx = 0, ny2 = 0, nz = 0;
            try {
              const n = J.wrapPointer(inManifold, J.ContactManifold).mWorldSpaceNormal;
              nx = n.GetX(); ny2 = n.GetY(); nz = n.GetZ();
            } catch { /* no normal → fall through with the unprojected drive */ }
            const dot = d[0] * nx + d[1] * ny2 + d[2] * nz;      // component INTO the rail — meaningless for a surface
            const k = PHYS.railAssist;
            this._rail.Set((d[0] - dot * nx) * k, (d[1] - dot * ny2) * k, (d[2] - dot * nz) * k);
            v = this._rail;
          }
        }
      }
      if (v) J.wrapPointer(ioSettings, J.ContactSettings).mRelativeLinearSurfaceVelocity = v;
      // CONTACT ADDRESSING: remember which belt/machine cell this ore is actually touching, and how MUCH of it is
      // on that cell (contact-point count). This is what lets the economy find an ore riding off-center after a
      // corner, instead of deriving a tile from its center and losing it. Straddling ore resolve to the cell they
      // are most on — the same thing a Roblox `Touched` upgrader would report.
      const cell1 = this.beltCell.get(id1), cell2 = this.beltCell.get(id2);
      const cell = cell1 ?? cell2;
      if (cell) {
        const oreId = cell1 ? id2 : id1;
        let wgt = 1;
        try { wgt = J.wrapPointer(inManifold, J.ContactManifold).mRelativeContactPointsOn1.size() || 1; } catch { /* weight stays 1 */ }
        const prev = this.oreCell.get(oreId);
        if (!prev || wgt > prev.w) this.oreCell.set(oreId, { cell, w: wgt });
      }
      if (this.probe) { // DEV only — census of what each ore is in contact with, plus how level that contact is
        let ny = 1;
        try { ny = Math.abs(J.wrapPointer(inManifold, J.ContactManifold).mWorldSpaceNormal.GetY()); } catch { /* keep 1 */ }
        // CURB and WALL are worth telling apart: a wall is something the player placed and can see, a curb is
        // an invisible auto-generated lane edge. Reporting "held by a wall" where there are no walls sends the
        // reader looking for a building that does not exist.
        const kindOf = (id: number) =>
          this.beltCell.has(id) ? "belt" : this.wallId.has(id) ? (this.wallId.get(id)!.startsWith("curb:") ? "curb" : "wall") : "ore";
        const k1 = kindOf(id1), k2 = kindOf(id2);
        const note = (oreId: number, other: "belt" | "wall" | "curb" | "ore") => {
          let r = this.probe!.get(oreId);
          if (!r) { r = { belt: 0, wall: 0, curb: 0, ore: 0, nyMin: 1 }; this.probe!.set(oreId, r); }
          r[other]++; r.nyMin = Math.min(r.nyMin, ny);
        };
        if (k1 === "ore") note(id1, k2);
        if (k2 === "ore") note(id2, k1);
      }
    };
    cl.OnContactAdded = (b1: number, b2: number, m: number, s: number) => stamp(b1, b2, m, s);
    cl.OnContactPersisted = (b1: number, b2: number, m: number, s: number) => stamp(b1, b2, m, s);
    cl.OnContactRemoved = () => {};
    cl.OnContactValidate = () => J.ValidateResult_AcceptAllContactsForThisBodyPair;
    this.ps.SetContactListener(cl);
    this.listener = cl; // held so the GC can't collect the listener while the PhysicsSystem still calls it
  }

  static async create(): Promise<JoltWorld> {
    const J = await loadJolt();
    return new JoltWorld(J);
  }

  /** Register a STATIC belt slab under cell `cellKey` whose surface drives resting ore along `dir` at oreSpeed.
   *  `cx/cz` = belt center (game); `deckY` = the height ore ride at (belt TOP lands ORE_HALF below it). `halfX/halfZ`
   *  size the footprint (50=1 tile; the 2-tile waverider passes 100 along its axis).
   *
   *  IDEMPOTENT ON THE *PARAMETERS*, not merely on the cell key. It used to early-out on `belts.has(cellKey)`,
   *  which meant a deck's surface velocity was written once at body creation and NEVER revised: any layout change
   *  that removed and re-added a belt on the same cell inside ONE frame (`importCode` is exactly that — clearLayout
   *  + the whole place loop in a single call, while syncBelts runs once per frame) kept the OLD heading forever.
   *  The model, the ghost arrow and every bit of game logic showed the new direction; only the physics deck was
   *  wrong. The Triplexer was the acute case — it is the only building whose three cells do not all share `b.dir`
   *  — and a corner left pointing `dir+2` drives ore head-on back into the entry stream, which is the reported
   *  "ore stall on the Triplexer's turn". Comparing a signature fixes the whole class, not just that symptom. */
  registerBelt(cellKey: string, cx: number, deckY: number, cz: number, dir: Dir, halfX = 50, halfZ = 50, speed: number = FACTORY.oreSpeed): void {
    const sig = `b|${cx}|${deckY}|${cz}|${dir}|${halfX}|${halfZ}|${speed}`;
    if (this.beltSig.get(cellKey) === sig) return;          // same body as last sync → nothing to do
    if (this.belts.has(cellKey)) this.unregisterBelt(cellKey); // changed → the old body is WRONG, rebuild it
    this.beltSig.set(cellKey, sig);
    const J = this.J;
    this.ensureListener();
    const shape = new J.BoxShape(new J.Vec3((halfX + PHYS.beltPad) / SCALE, BELT_HALF_Y / SCALE, (halfZ + PHYS.beltPad) / SCALE), 0.02);
    const cy = deckY - oreHalf() - BELT_HALF_Y; // belt top = cy + BELT_HALF_Y = deckY - ORE_HALF → ore center rests AT deckY
    this._rp.SetX(cx / SCALE); this._rp.SetY(cy / SCALE); this._rp.SetZ(cz / SCALE);
    const bcs = new J.BodyCreationSettings(shape, this._rp, this._iq, J.EMotionType_Static, L_STATIC);
    bcs.mFriction = PHYS.beltFriction; // grip the ore so the surface velocity actually carries it
    bcs.mRestitution = PHYS.restitution;
    const body = this.createStatic(bcs);
    J.destroy(bcs);
    if (!body) { this.beltSig.delete(cellKey); return; } // budget spent — drop the sig so a later sync can retry
    this.bi.AddBody(body.GetID(), J.EActivation_DontActivate);
    const [dx, dz] = DIR_VEC[dir];
    // speed 0 = an INERT deck (splitter/pressurizer): ore rest on it, but the tile's own scripted logic drives them.
    if (speed !== 0) {
      const sv = new J.Vec3((speed * dx) / SCALE, 0, (speed * dz) / SCALE);
      this.beltVel.set(body.GetID().GetIndexAndSequenceNumber(), sv);
      this.cellVel.set(cellKey, sv); // same Vec3, reused for the guide-rail contacts beside this deck
    }
    this.beltCell.set(body.GetID().GetIndexAndSequenceNumber(), cellKey);
    this.belts.set(cellKey, body);
  }

  /** Register a STATIC INCLINED conveyor — a ramp as a REAL sloped surface, not a scripted rail. Ore climb it by
   *  friction against the surface velocity, and are free to jostle, queue, pile and spill off the sides, exactly
   *  like a Roblox tycoon's ramp part. `s*` is the midpoint of the RIDE surface, `n*` its unit normal (the slab is
   *  sunk below it so an ore's center rides at the surface), `q*` the slope rotation, `v*` the surface velocity. */
  registerRamp(cellKey: string, sx: number, sy: number, sz: number, nx: number, ny: number, nz: number,
               qx: number, qy: number, qz: number, qw: number,
               halfAlong: number, halfSide: number, vx: number, vy: number, vz: number): void {
    // Same signature guard as registerBelt — a re-imported ramp used to keep its old slope AND heading.
    const sig = `r|${sx}|${sy}|${sz}|${nx}|${ny}|${nz}|${qx}|${qy}|${qz}|${qw}|${halfAlong}|${halfSide}|${vx}|${vy}|${vz}`;
    if (this.beltSig.get(cellKey) === sig) return;
    if (this.belts.has(cellKey)) this.unregisterBelt(cellKey);
    this.beltSig.set(cellKey, sig);
    const J = this.J;
    this.ensureListener();
    const drop = oreHalf() + BELT_HALF_Y; // sink the slab along its normal so the ride surface sits where ore rest
    const shape = new J.BoxShape(new J.Vec3(halfAlong / SCALE, BELT_HALF_Y / SCALE, halfSide / SCALE), 0.02);
    this._rp.SetX((sx - nx * drop) / SCALE); this._rp.SetY((sy - ny * drop) / SCALE); this._rp.SetZ((sz - nz * drop) / SCALE);
    const rot = new J.Quat(qx, qy, qz, qw);
    const bcs = new J.BodyCreationSettings(shape, this._rp, rot, J.EMotionType_Static, L_STATIC);
    bcs.mFriction = PHYS.beltFriction; bcs.mRestitution = PHYS.restitution; // grip: tan(18.4°)=0.33 well under 0.9, so ore climb without slipping
    const body = this.createStatic(bcs);
    J.destroy(bcs); J.destroy(rot);
    if (!body) { this.beltSig.delete(cellKey); return; } // budget spent — drop the sig so a later sync can retry
    this.bi.AddBody(body.GetID(), J.EActivation_DontActivate);
    const rv = new J.Vec3(vx / SCALE, vy / SCALE, vz / SCALE);
    this.beltVel.set(body.GetID().GetIndexAndSequenceNumber(), rv);
    this.cellVel.set(cellKey, rv);
    this.beltCell.set(body.GetID().GetIndexAndSequenceNumber(), cellKey);
    this.belts.set(cellKey, body);
  }

  /** Remove a belt when its cell is bulldozed / re-themed (rare → churn is fine, unlike ore). */
  unregisterBelt(cellKey: string): void {
    const body = this.belts.get(cellKey);
    if (!body) return;
    const id = body.GetID(), bid = id.GetIndexAndSequenceNumber();
    const v = this.beltVel.get(bid);
    if (v) { this.J.destroy(v); this.beltVel.delete(bid); }
    this.cellVel.delete(cellKey);
    this.beltCell.delete(bid);
    this.bi.RemoveBody(id);
    this.bi.DestroyBody(id);
    this.staticCount--;
    this.belts.delete(cellKey);
    this.beltSig.delete(cellKey);
  }

  hasBelt(cellKey: string): boolean { return this.belts.has(cellKey); }
  beltKeys(): string[] { return [...this.belts.keys()]; }

  /** Register a STATIC wall slab on a tile edge. In the legacy integrator walls were a px/pz clamp that only ore
   *  "knew" about; as real bodies they finally CONTAIN the pile — flung and backed-up ore can't spill through. */
  registerWall(k: string, cx: number, cy: number, cz: number, hx: number, hy: number, hz: number): void {
    if (this.wallBodies.has(k)) return;
    const J = this.J;
    const shape = new J.BoxShape(new J.Vec3(hx / SCALE, hy / SCALE, hz / SCALE), 0.02);
    this._rp.SetX(cx / SCALE); this._rp.SetY(cy / SCALE); this._rp.SetZ(cz / SCALE);
    const bcs = new J.BodyCreationSettings(shape, this._rp, this._iq, J.EMotionType_Static, L_STATIC);
    bcs.mFriction = PHYS.wallFriction; bcs.mRestitution = 0; // ore slide along a wall rather than sticking or pinging off
    const body = this.createStatic(bcs);
    J.destroy(bcs);
    if (!body) return;          // budget spent — this wall simply has no collider; ore can pass it
    this.bi.AddBody(body.GetID(), J.EActivation_DontActivate);
    this.wallId.set(body.GetID().GetIndexAndSequenceNumber(), k);
    this.wallBodies.set(k, body);
  }

  unregisterWall(k: string): void {
    const body = this.wallBodies.get(k);
    if (!body) return;
    const id = body.GetID();
    this.wallId.delete(id.GetIndexAndSequenceNumber());
    this.bi.RemoveBody(id);
    this.bi.DestroyBody(id);
    this.staticCount--;
    this.wallBodies.delete(k);
  }

  hasWall(k: string): boolean { return this.wallBodies.has(k); }
  wallKeys(): string[] { return [...this.wallBodies.keys()]; }

  /** DEV: start/stop gathering the contact census (see `probe`). Off costs nothing. Calling this with `true`
   *  RESETS the census, so ask isProbing() before re-arming or you wipe the current step's data every frame. */
  setProbe(on: boolean): void { this.probe = on ? new Map() : null; }

  isProbing(): boolean { return this.probe != null; }

  /** DEV: what this ore touched on the last step — belt/wall/ore contact counts, and `nyMin`, the most
   *  horizontal contact normal it had (1 = resting flat on a deck, ~0 = pressed against a vertical face). */
  probeOf(h: OreHandle): { belt: number; wall: number; curb: number; ore: number; nyMin: number } | null {
    const body = this.bodies.get(h);
    if (!body || !this.probe) return null;
    return this.probe.get(body.GetID().GetIndexAndSequenceNumber()) ?? null;
  }

  /** Spawn a dynamic ore body at a GAME-space position; returns the handle to store on the Item (null if the
   *  pool is full — never let CreateBody run past mMaxBodies, which faults the WASM heap rather than failing soft). */
  spawnOre(x: number, y: number, z: number): OreHandle | null {
    const body = this.freeList.pop();
    if (!body) return null; // pool exhausted (>oreCap live) — retry next frame
    const J = this.J, id = body.GetID();
    this._rp.SetX(x / SCALE); this._rp.SetY(y / SCALE); this._rp.SetZ(z / SCALE);
    this._sv.SetX(0); this._sv.SetY(0); this._sv.SetZ(0);
    this.bi.SetPositionAndRotation(id, this._rp, this._iq, J.EActivation_Activate);
    this.bi.SetLinearVelocity(id, this._sv);
    body.GetMotionProperties().SetMassProperties(this.dof, this.oreShape.GetMassProperties()); // reset no-tumble (undo any prior fling)
    const h = this.nextH++;
    this.bodies.set(h, body);
    return h;
  }

  /** Retire an ore body (reached the furnace, destroyed by a waverider, or fell off): park + deactivate + return
   *  it to the free-list for reuse. No DestroyBody — the pool is fixed for the world's life. */
  despawnOre(h: OreHandle): void {
    const body = this.bodies.get(h);
    if (!body) return;
    const id = body.GetID();
    this._sv.SetX(0); this._sv.SetY(0); this._sv.SetZ(0);
    this.bi.SetLinearVelocity(id, this._sv);
    this._rp.SetX(0); this._rp.SetY(this.PARK); this._rp.SetZ(0);
    this.bi.SetPositionAndRotation(id, this._rp, this._iq, this.J.EActivation_DontActivate);
    this.bi.DeactivateBody(id); // DontActivate won't STOP an already-awake body — parked ore would keep simulating (and falling) forever
    this.bodies.delete(h);
    this.freeList.push(body);
  }

  /** Fling: wake the body, unlock full tumble, and apply a velocity kick (GAME units). */
  applyFling(h: OreHandle, vx: number, vy: number, vz: number): void {
    const body = this.bodies.get(h);
    if (!body) return;
    const J = this.J, id = body.GetID();
    this.bi.ActivateBody(id);
    body.GetMotionProperties().SetMassProperties(J.EAllowedDOFs_All, this.oreShape.GetMassProperties()); // full 6-DOF so it arcs & tumbles; re-locked no-tumble on respawn
    this._sv.SetX(vx / SCALE); this._sv.SetY(vy / SCALE); this._sv.SetZ(vz / SCALE);
    this.bi.AddLinearVelocity(id, this._sv);
  }

  /** Directly drive a body's state (a kinematic-style override on the dynamic body) — used for the RAMP climb,
   *  where ore follow a fixed slope rather than free physics. Teleports the body + sets its velocity; call each
   *  frame while the ore is on the ramp so gravity can't pull it back down the incline. */
  setBody(h: OreHandle, x: number, y: number, z: number, vx: number, vy: number, vz: number): void {
    const body = this.bodies.get(h);
    if (!body) return;
    const id = body.GetID();
    this._rp.SetX(x / SCALE); this._rp.SetY(y / SCALE); this._rp.SetZ(z / SCALE);
    this.bi.SetPositionAndRotation(id, this._rp, this._iq, this.J.EActivation_Activate);
    this._sv.SetX(vx / SCALE); this._sv.SetY(vy / SCALE); this._sv.SetZ(vz / SCALE);
    this.bi.SetLinearVelocity(id, this._sv);
  }

  /** Steer a body without moving it — the scripted drive for inert decks (splitter routing, pressurizer intake). */
  setVelocity(h: OreHandle, vx: number, vy: number, vz: number): void {
    const body = this.bodies.get(h);
    if (!body) return;
    this._sv.SetX(vx / SCALE); this._sv.SetY(vy / SCALE); this._sv.SetZ(vz / SCALE);
    this.bi.SetLinearVelocity(body.GetID(), this._sv);
  }

  /** Nudge an ore's horizontal velocity TOWARD the belt's travel velocity (game units), by fraction `k` per call.
   *  A friction-only conveyor can't turn a fast ore at a corner, so it sails off the edge. Blending toward the belt
   *  (what the legacy integrator did) curves the ore around corners while KEEPING it moving — an earlier version
   *  zeroed the across-axis component instead, which stopped ore dead at every 90° turn and jammed them. A small
   *  `k` also leaves most of the solver's collision response intact, so piles still resolve normally. */
  steerToward(h: OreHandle, tvx: number, tvz: number, k: number): void {
    const body = this.bodies.get(h);
    if (!body) return;
    const id = body.GetID();
    const v = this.bi.GetLinearVelocity(id);
    const vx = v.GetX(), vy = v.GetY(), vz = v.GetZ(); // [Value] temp — read all three before any other Jolt call
    this._sv.SetX(vx + (tvx / SCALE - vx) * k);
    this._sv.SetY(vy);
    this._sv.SetZ(vz + (tvz / SCALE - vz) * k);
    this.bi.SetLinearVelocity(id, this._sv);
  }

  /** Re-apply the no-tumble DOF lock after a fling let the ore tumble freely (called once, when it lands). */
  relockNoTumble(h: OreHandle): void {
    const body = this.bodies.get(h);
    if (!body) return;
    body.GetMotionProperties().SetMassProperties(this.dof, this.oreShape.GetMassProperties());
  }

  /** Advance physics with a fixed-dt accumulator; returns how many fixed steps ran (0 if sub-frame). */
  /** The ONE timestep the simulation advances by. Everything that changes the world runs on this, never on a
   *  frame's dt — see `step`'s onStep hook. */
  get fixedStep(): number { return this.fixed; }

  /** Advance by `dt` of wall time in fixed increments, calling `onStep(fixed)` after each one. Callers put ALL
   *  simulation work in that callback: a frame is a variable, hardware-dependent slice of time, so anything
   *  scaled by it (cooldowns, scripted drives, economy passes) drifts with frame rate. Inside onStep the
   *  timestep is constant, so the sim is identical at 20fps and 144fps. Frames only read the result. */
  step(dt: number, onPre?: (fixed: number) => void, onPost?: (fixed: number) => void): number {
    this.accum += Math.min(dt, 0.1); // clamp a long stall so we don't spiral
    let n = 0;
    while (this.accum >= this.fixed) {
      onPre?.(this.fixed);
      const swap = this.oreCellPrev; this.oreCellPrev = this.oreCell; this.oreCell = swap;
      this.oreCell.clear();
      const dswap = this.oreDrivePrev; this.oreDrivePrev = this.oreDrive; this.oreDrive = dswap;
      this.oreDrive.clear();
      this.probe?.clear();
      this.jolt.Step(this.fixed, 1); this.accum -= this.fixed; n++;
      onPost?.(this.fixed);
    }
    return n;
  }

  /** Read a body's GAME-space position back into an ore's px/py/pz (call each frame for active ore). */
  readInto(h: OreHandle, o: { px: number; py: number; pz: number; qx: number; qy: number; qz: number; qw: number }): void {
    const body = this.bodies.get(h);
    if (!body) return;
    const id = body.GetID();
    const p = this.bi.GetPosition(id); // [Value] getter reuses a temporary — read components NOW
    o.px = p.GetX() * SCALE; o.py = p.GetY() * SCALE; o.pz = p.GetZ() * SCALE;
    // ORIENTATION TOO. With tumble on the bodies are full 6-DOF and have been rotating all along — this used
    // to read position only and the renderer drew every ore axis-aligned, so the sim looked direction-locked
    // when it wasn't. A tumbling ore you cannot see is just a cube sliding about.
    const q = this.bi.GetRotation(id);
    o.qx = q.GetX(); o.qy = q.GetY(); o.qz = q.GetZ(); o.qw = q.GetW();
  }

  /** Which belt/machine CELL ("gx,gz,floor") this ore is actually resting on, straight from the physics contacts —
   *  or null if it is airborne. The economy addresses buildings through this instead of the ore's center tile. */
  contactCell(h: OreHandle): string | null {
    const body = this.bodies.get(h);
    if (!body) return null;
    const rec = this.oreCell.get(body.GetID().GetIndexAndSequenceNumber());
    return rec ? rec.cell : null;
  }

  /** Drop a body's contact record so `contactCell` reports "airborne" until the physics actually steps again.
   *
   *  WHY THIS EXISTS. `fixed` is 1/30 but `update()` runs at 60 fps, so `step()` only swaps+clears the contact
   *  census on ALTERNATE frames (measured: 200 stepping / 200 not, over 400 frames). On a non-stepping frame
   *  `contactCell` therefore returns the PREVIOUS step's cell — a one-frame lie that is harmless for a machine
   *  which stamps `capKey` with its OWN tile, but fatal for one that WARPS the ore elsewhere: the frame right
   *  after a warp is always a non-stepping frame, so the ore reports "still touching the entrance" while sitting
   *  on the exit pad, and fires the machine a second time. That is exactly what made teleporter A double-jump
   *  every ore (net cursor advance of 2 → with an EVEN number of exits, half the pads never received anything).
   *  Call this immediately after any `setBody` warp; the economy then falls back to the freshly-written position. */
  forgetContact(h: OreHandle): void {
    const body = this.bodies.get(h);
    if (!body) return;
    const bid = body.GetID().GetIndexAndSequenceNumber();
    this.oreCell.delete(bid);
    this.oreCellPrev.delete(bid);
  }

  /** REAL world-space AABBs of every registered belt/ramp/curb body, in GAME units. The factory's `cellDeckY` is
   *  only a MODEL of where surfaces are; when a body is actually misplaced (a mis-seated ramp slab, a bad yaw)
   *  nothing catches it because the model still reads correctly. This lets a diagnostic compare the two. */
  bodyBounds(): Record<string, { min: number[]; max: number[] }> {
    const out: Record<string, { min: number[]; max: number[] }> = {};
    const add = (k: string, body: J) => {
      const b = body.GetWorldSpaceBounds(), mn = b.mMin, mx = b.mMax;
      out[k] = { min: [mn.GetX() * SCALE, mn.GetY() * SCALE, mn.GetZ() * SCALE],
                 max: [mx.GetX() * SCALE, mx.GetY() * SCALE, mx.GetZ() * SCALE] };
    };
    for (const [k, b] of this.belts) add(k, b);
    for (const [k, b] of this.wallBodies) add(k, b);
    return out;
  }

  get activeBodies(): number {
    return this.ps.GetNumActiveBodies(this.J.EBodyType_RigidBody);
  }

  teardown(): void {
    this.J.destroy(this.jolt); // frees the PhysicsSystem + every pooled body + temp allocator in one shot
    if (this.listener) this.J.destroy(this.listener);
    this.beltVel.clear();
    this.beltCell.clear();
    this.cellVel.clear();
    this.oreCell.clear();
    this.oreCellPrev.clear();
    this.oreDrive.clear();
    this.oreDrivePrev.clear();
    this.belts.clear();
    this.beltSig.clear();
    this.wallBodies.clear();
    this.wallId.clear();
    this.bodies.clear();
    this.freeList.length = 0;
  }
}
